import { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
} from "react-router-dom";
import { fetchUserProfile } from "../store/slices/authSlice";

// Import layouts
import AuthLayout from "../layouts/AuthLayout";
import ProtectedLayout from "../layouts/ProtectedLayout";

// Import auth pages
import AcceptInvitePage from "../pages/auth/accept-invite/accept-invite";
import Login from "../pages/auth/login/login";

// Import main pages
import NotFound from "../pages/not-found/not-found";
import ProfilePage from "../pages/profile/profile";
import Projects from "../pages/projects/projects";

// Import settings pages
import ProjectDetailsLayout from "../pages/projects/ProjectDetailsLayout";
import AddAufmassV2 from "../pages/projects/aufmass/add-aufmass-v2";
import ProjectCompanies from "../pages/projects/companies/project-companies";
import CostTrackingPage from "../pages/projects/cost-tracking/cost-tracking";
import AddInvoiceV2 from "../pages/projects/invoice/add-invoice-v2";
import ProjectInvoicePage from "../pages/projects/invoice/project-invoice";
import BoQEditorPage from "../pages/projects/boq/boq-editor-page";
import LVCostGroupAssignmentPage from "../pages/projects/lv/cost-group-assignment-page";
import ProjectLVPage from "../pages/projects/lv/project-lv";
import ViewLVPage from "../pages/projects/lv/view-lv-page";
import ProjectMembers from "../pages/projects/members/project-members";
import PermissionPresetsPage from "../pages/projects/permission-presets";
import ProjectOverview from "../pages/projects/overview/project-overview";
import ProjectPlanning from "../pages/projects/planning/planning";
import SupplierInfoPage from "../pages/projects/supplier-info";
import ProjectTicketCategories from "../pages/projects/ticket-categories/project-ticket-categories";
import ProjectTicketsPage from "../pages/projects/tickets/project-tickets";
import PermissionTemplatesAdminPage from "../pages/admin/permission-templates";
import ProjectsOverviewAdminPage from "../pages/admin/projects-overview";
import AdminRoute from "../components/AdminRoute";
import CapmoSyncPage from "../pages/settings/capmo-sync/capmo-sync";
import CompanySettingPage from "../pages/settings/company/company";
import RoleSettingPage from "../pages/settings/role/role";
import TemplateSettingPage from "../pages/settings/template/template";
import UserSettingPage from "../pages/settings/user/user";

// Loading fallback component
const LoadingFallback = () => <div className="loading-screen">Loading...</div>;

const AppRoutes = () => {
	const dispatch = useDispatch();
	const { isAuthenticated, loading } = useSelector((state) => state.auth);

	// Attempt to fetch user profile if token exists but no user data
	useEffect(() => {
		if (isAuthenticated) {
			dispatch(fetchUserProfile());
		}
	}, [isAuthenticated, dispatch]);

	// Only block rendering during the initial profile fetch, not during OTP operations.
	// Blocking on all loading states unmounts the Login component and destroys its step state.
	if (loading === "fetchProfile" && !isAuthenticated) {
		return <LoadingFallback />;
	}

	return (
		<Router>
			<Suspense fallback={<LoadingFallback />}>
				<Routes>
					{/* Auth routes */}
					<Route element={<AuthLayout />}>
						<Route path="/login" element={<Login />} />
					</Route>

					<Route element={<ProtectedLayout />}>
						<Route
							path="/"
							element={<Navigate to="/projects" replace />}
						/>
						<Route path="/projects" element={<Projects />} />

						<Route
							path="/projects/:projectId"
							element={<ProjectDetailsLayout />}
						>
							<Route index element={<ProjectOverview />} />
							<Route
								path="ticket-categories"
								element={<ProjectTicketCategories />}
							/>
							<Route
								path="tickets"
								element={<ProjectTicketsPage />}
							/>
							<Route path="lv" element={<ProjectLVPage />} />
							<Route
								path="lv/:lvId/edit"
								element={<BoQEditorPage />}
							/>
							<Route
								path="lv/:lvId/view"
								element={<ViewLVPage />}
							/>
							<Route
								path="lv/:lvId/cost-groups"
								element={<LVCostGroupAssignmentPage />}
							/>
							<Route
								path="invoice"
								element={<ProjectInvoicePage />}
							/>
							<Route
								path="invoice-v2"
								element={<AddInvoiceV2 mode="add" />}
							/>
							<Route
								path="invoice-v2/edit/:invoiceId"
								element={<AddInvoiceV2 mode="edit" />}
							/>
							<Route
								path="invoice-v2/view/:invoiceId"
								element={<AddInvoiceV2 mode="view" />}
							/>
							<Route
								path="invoice/supplier-info/:invoiceId"
								element={<SupplierInfoPage />}
							/>
							<Route
								path="members"
								element={<ProjectMembers />}
							/>
							<Route
								path="permission-presets"
								element={<PermissionPresetsPage />}
							/>
							<Route
								path="companies"
								element={<ProjectCompanies />}
							/>
							<Route
								path="cost-tracking"
								element={<CostTrackingPage />}
							/>
							<Route
								path="invoice/aufmass-v2"
								element={<AddAufmassV2 mode="add" />}
							/>
							<Route
								path="invoice/aufmass-v2/edit/:aufmassId"
								element={<AddAufmassV2 mode="edit" />}
							/>
							<Route
								path="invoice/aufmass-v2/view/:aufmassId"
								element={<AddAufmassV2 mode="view" />}
							/>
							<Route
								path="planning"
								element={<ProjectPlanning />}
							/>
						</Route>
						<Route path="/profile" element={<ProfilePage />} />

						{/* Settings routes - direct components */}
						<Route
							path="/settings/user"
							element={<UserSettingPage />}
						/>
						<Route
							path="/admin/users"
							element={<Navigate to="/settings/user" replace />}
						/>
						<Route
							path="/settings/role"
							element={<RoleSettingPage />}
						/>
						<Route
							path="/settings/company"
							element={<CompanySettingPage />}
						/>
						<Route
							path="/settings/templates"
							element={<TemplateSettingPage />}
						/>
						<Route
							path="/settings/capmo-sync"
							element={<CapmoSyncPage />}
						/>
						<Route
							path="/settings/permission-templates"
							element={<PermissionTemplatesAdminPage />}
						/>
						<Route
							path="/admin/permission-templates"
							element={
								<Navigate to="/settings/permission-templates" replace />
							}
						/>
						<Route
							path="/admin/projects-overview"
							element={
								<AdminRoute>
									<ProjectsOverviewAdminPage />
								</AdminRoute>
							}
						/>
					</Route>

					{/* Public accept-invite route - must sit outside Auth/Protected
					    layouts so both anonymous and logged-in recipients can hit
					    it without being bounced. */}
					<Route
						path="/accept-invite"
						element={<AcceptInvitePage />}
					/>

					{/* Redirect to login if not authenticated, otherwise show 404 */}
					<Route
						path="*"
						element={
							isAuthenticated ? (
								<NotFound />
							) : (
								<Navigate to="/login" />
							)
						}
					/>
				</Routes>
			</Suspense>
		</Router>
	);
};

export default AppRoutes;
